<template>
  <div
    class="flex flex-col h-screen border-l border-[#E5E5E5]"
    :class="componentClasses"
  >
    <div class="w-full p-4">
      <div class="flex justify-between">
        <div
          v-if="!platformStore.isSidebarCollapsed"
          class="font-semibold font-primary text-[18px] leading-[initial] text-gray-800 line-clamp-4"
        >
          Archived copyrights
        </div>
        <Icon
          :size="18"
          name="Upload"
          class="cursor-pointer stroke-gray-800"
          :class="collapseIconClasses"
          @click="platformStore.toggleSidebarCollapse"
        />
      </div>
    </div>
    <Accordion
      v-if="!platformStore.isSidebarCollapsed"
      :key="props.selectedItem?.id"
    >
      <AccordionItem id="information">
        <AccordionHeader>
          <Icon :size="16" name="Info" class="stroke-gray-800 mr-2" />
          <div
            class="font-semibold font-primary text-[14px] leading-[20px] text-gray-800 tracking-[-0.18px]"
          >
            Information
          </div>
        </AccordionHeader>
        <AccordionContent class="overflow-y-auto pb-3">
          <div class="w-full h-[176px] p-[9px] bg-[#FAFAFA] rounded-md mb-6">
            <ThumbnailIcon
              class="w-full h-full"
              :icon-size="140"
              :type="props.selectedItem?.type || ItemType.Folder"
              :image-url="props.selectedItem?.imageUrl"
            />
          </div>
          <template v-if="props.selectedItem">
            <div
              v-for="(info, index) in information"
              :key="index"
              class="w-full h-[30px] font-primary border-b border-[#E5E5E5] flex items-center justify-between text-[12px] leading-[16px] text-gray-800"
            >
              <div class="opacity-60 -mt-[1px]">{{ info.label }}</div>
              <div class="font-secondary -mt-[1px]">{{ info.value }}</div>
            </div>
          </template>
          <template v-else>
            <Skeleton v-if="loading" class="w-full h-[13px]" />
            <template v-else>
              <div
                class="w-full h-[30px] font-primary border-b border-[#E5E5E5] flex items-center justify-between text-[12px] leading-[16px] text-gray-800"
              >
                <div class="flex items-center opacity-60">
                  <div>Number of files</div>
                </div>
                <div class="font-secondary">
                  {{ props.total }}
                </div>
              </div>
            </template>
          </template>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem v-if="props.selectedItem" id="transactions">
        <AccordionHeader>
          <Icon :size="16" name="Clock" class="stroke-gray-800 mr-2" />
          <div
            class="font-semibold font-primary text-[14px] leading-[20px] text-gray-800 tracking-[-0.18px]"
          >
            Transaction History
          </div>
        </AccordionHeader>
        <AccordionContent class="overflow-y-auto pb-3">
          <TransactionCard
            v-for="(transaction, index) in props.selectedItem.transactions"
            :key="index"
            :transaction="transaction"
            class="mb-2.5 last:mb-0"
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionContent,
} from '@/shared/components/Accordion'
import { ThumbnailIcon, Skeleton, Icon } from '@dacr/ui'
import TransactionCard from '@/features/billing/components/TransactionCard.vue'
import { ItemType, type ArchivedFile } from '@/shared/graphql/legacy/generated'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useDevice } from '@/shared/composables/useDevice'
import { usePlatformStore } from '@/shared/stores/platform'

const props = defineProps<{
  selectedItem?: ArchivedFile
  total?: number | null
}>()

const device = useDevice()
const platformStore = usePlatformStore()

const loading = computed(
  () => props.total === undefined || props.total === null
)

if (device.isTabletHorizontal.value) {
  platformStore.setSidebarCollapse(true)
}

watch(
  () => props.selectedItem,
  () => {
    if (document.body.style.pointerEvents === 'none' || device.isDesktop.value)
      return

    platformStore.setSidebarCollapse(!props.selectedItem)
  }
)

const componentClasses = computed(() => ({
  ...(platformStore.isSidebarCollapsed && {
    'min-w-[50px]': true,
    'max-w-[50px]': true,
  }),
  ...(!platformStore.isSidebarCollapsed && {
    'min-w-[330px]': true,
    'max-w-[330px]': true,
    'tablet-h:[box-shadow:0_0_20px_rgba(0,0,0,0.1)]':
      device.isTabletHorizontal.value,
    'tablet-h:[clip-path:inset(0_0_0_-20px)]': device.isTabletHorizontal.value,
  }),
}))

const collapseIconClasses = computed(() => ({
  ...(platformStore.isSidebarCollapsed && { '-rotate-90': true }),
  ...(!platformStore.isSidebarCollapsed && { 'rotate-90': true }),
}))

const information = computed(() => [
  {
    label: 'Located in',
    value: 'Archive',
  },
  {
    label: 'Archived at',
    value: formatTimestamp(props.selectedItem?.archivedAt),
  },
  {
    label: 'Size',
    value: props.selectedItem?.size,
  },
  {
    label: 'Type',
    value: props.selectedItem?.friendlyType,
  },
])
</script>
